'use client';
import { useEffect, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface HousingType {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  benefits: string[];
  image: string;
  imageAlt: string;
  icon: string;
  accentColor: string;
  accentBorder: string;
}

const housingTypes: HousingType[] = [
  {
    id: 'type-1',
    title: 'Eengezinswoning',
    subtitle: 'Rijtjeshuizen, twee-onder-een-kap, vrijstaand',
    description: 'Perfect voor elke woning. In 1 uur geïnstalleerd, ook achteraf.',
    benefits: ['Eenvoudige installatie', 'Geschikt voor alle leidingmaten', 'Geen verbouwing nodig'],
    image: '/assets/images/424x318_crop-1773844907255.jpg',
    imageAlt: 'Modern row houses eengezinswoning in Dutch neighborhood',
    icon: 'HomeIcon',
    accentColor: 'from-[#0584F2] to-[#33BEBE]',
    accentBorder: 'border-[#0584F2]/30',
  },
  {
    id: 'type-2',
    title: 'Appartement',
    subtitle: 'Individuele units voor appartementen in een VvE',
    description: 'Ook in appartementen eenvoudig te plaatsen. Compact en effectief.',
    benefits: ['Compact ontwerp', 'VvE-goedkeuring mogelijk', 'Geen gedeelde installatie'],
    image: '/assets/images/a45567818829d3c8e9624bb9b6e16aad-1773845081302.jpg',
    imageAlt: 'Modern white apartment complex with balconies and garden',
    icon: 'BuildingOfficeIcon',
    accentColor: 'from-[#33BEBE] to-[#0584F2]',
    accentBorder: 'border-[#33BEBE]/30',
  },
  {
    id: 'type-3',
    title: 'Zakelijk',
    subtitle: 'Horeca, kantoren, hotels, zorginstellingen',
    description: 'Schaalbare oplossingen voor professioneel gebruik. Langdurige garantie.',
    benefits: ['Industriële capaciteit', 'Minimale downtime', 'Professionele service'],
    image: 'https://images.unsplash.com/photo-1724906019900-601545c07107',
    imageAlt: 'Modern office building interior with open workspace and natural light',
    icon: 'BriefcaseIcon',
    accentColor: 'from-[#2F4F6E] to-[#0584F2]',
    accentBorder: 'border-[#2F4F6E]/50',
  },
];

export default function HousingTypeSection() {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const handleSelectType = (typeTitle: string) => {
    const element = document.getElementById('contact-form');
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
      setTimeout(() => {
        const selectElement = document.getElementById('housing-type-select') as HTMLSelectElement;
        if (selectElement) {
          selectElement.value = typeTitle;
          selectElement.focus();
        }
      }, 500);
    }
  };

  return (
    <section id="geschikt-voor" className="relative py-20 md:py-32 overflow-hidden">
      {/* Subtle radial accents — no hard background */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-[#0584F2]/05 via-transparent to-transparent pointer-events-none"></div>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-[#33BEBE]/04 via-transparent to-transparent pointer-events-none"></div>

      {/* Softened decorative glow elements */}
      <div className="absolute top-0 right-0 w-[480px] h-[480px] bg-gradient-to-bl from-[#0584F2]/05 to-transparent rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-[420px] h-[420px] bg-gradient-to-tr from-[#33BEBE]/04 to-transparent rounded-full blur-3xl"></div>

      <div className="relative mx-auto max-w-7xl px-6">
        {/* Section Header */}
        <div className="text-center space-y-4 mb-16 reveal">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#0584F2]/30 bg-[#0584F2]/10 backdrop-blur-md">
            <Icon name="HomeModernIcon" size={16} className="text-[#0584F2]" />
            <span className="text-xs font-bold uppercase tracking-widest text-[#0584F2]">
              Geschikt voor
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-heading font-extrabold tracking-tight bg-gradient-to-r from-[#0F1E2E] via-[#0584F2] to-[#33BEBE] bg-clip-text text-transparent">
            Voor elke situatie een oplossing
          </h2>
          <p className="text-lg max-w-2xl mx-auto" style={{ color: '#2F4F6E' }}>
            Of je nu in een rijtjeshuis, appartement of kantoor werkt — FrenaCal past overal.
          </p>
        </div>

        {/* Housing Type Cards — light with brand borders */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {housingTypes.map((type) => (
            <div
              key={type.id}
              className={`group rounded-2xl overflow-hidden border bg-white shadow-md hover:shadow-xl hover:-translate-y-2 transition-all duration-300 reveal`}
              style={{ borderColor: 'rgba(5,132,242,0.15)' }}
            >
              {/* Image */}
              <div className="relative h-52 overflow-hidden">
                <AppImage
                  src={type.image}
                  alt={type.imageAlt}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                {/* Light gradient overlay for depth */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0F1E2E]/40 via-transparent to-transparent"></div>
                {/* Icon badge */}
                <div className={`absolute top-4 right-4 h-12 w-12 rounded-xl bg-gradient-to-br ${type.accentColor} flex items-center justify-center shadow-lg`}>
                  <Icon name={type.icon as any} size={24} className="text-white" />
                </div>
              </div>

              {/* Content */}
              <div className="p-6 space-y-4">
                <div>
                  <h3 className={`text-2xl font-heading font-extrabold bg-gradient-to-r ${type.accentColor} bg-clip-text text-transparent mb-1`}>
                    {type.title}
                  </h3>
                  <p className="text-sm" style={{ color: '#2F4F6E' }}>{type.subtitle}</p>
                </div>

                <p className="text-sm leading-relaxed" style={{ color: '#2F4F6E' }}>{type.description}</p>

                {/* Benefits */}
                <ul className="space-y-2">
                  {type.benefits.map((benefit, index) => (
                    <li key={`${type.id}-benefit-${index}`} className="flex items-center gap-2 text-sm" style={{ color: '#2F4F6E' }}>
                      <Icon name="CheckCircleIcon" size={16} variant="solid" className="text-[#0584F2] shrink-0" />
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA Button */}
                {isHydrated && (
                  <button
                    onClick={() => handleSelectType(type.title)}
                    className={`w-full mt-4 inline-flex items-center justify-center rounded-full bg-gradient-to-r ${type.accentColor} px-6 py-3 text-sm font-bold text-white hover:opacity-90 hover:scale-105 transition-all duration-200 shadow-lg`}
                  >
                    Vraag advies aan
                    <Icon name="ArrowRightIcon" size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Additional Info — light card */}
        <div className="mt-16 rounded-2xl p-8 md:p-12 border border-[#0584F2]/15 bg-white shadow-md reveal">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="flex-1">
              <h3 className="text-2xl font-heading font-extrabold bg-gradient-to-r from-[#0F1E2E] to-[#33BEBE] bg-clip-text text-transparent mb-4">
                Twijfel je over de juiste oplossing?
              </h3>
              <p className="leading-relaxed" style={{ color: '#2F4F6E' }}>
                Geen probleem! Onze experts helpen je graag met een gratis, vrijblijvend adviesgesprek.
                We analyseren je situatie en adviseren de beste oplossing voor jouw woning of bedrijf.
              </p>
            </div>
            <div className="shrink-0">
              {isHydrated && (
                <button
                  onClick={() => handleSelectType('Advies nodig')}
                  className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-[#0584F2] to-[#33BEBE] px-8 py-4 text-base font-bold text-white hover:opacity-90 hover:scale-105 transition-all duration-200 shadow-xl"
                >
                  Gratis adviesgesprek
                  <Icon name="PhoneIcon" size={20} className="ml-2" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
